/**
 * Fetching the board.
 *
 * Specification: ../test/board-client.spec.ts. Master plan §4 M5.
 *
 * A FAILURE KEEPS WHAT THE SCREEN ALREADY HAS. That is not defensive habit: a
 * kitchen that loses the network mid-service still has food on the pass, and a
 * screen that blanks itself is worse than a screen that is a minute stale. The
 * tickets it holds were fired before the outage and still have to be cooked.
 */

import type { BoardTicket } from "./station";

export class BoardFetchError extends Error {
  /** The HTTP status, when the service answered: 402 is "this venue has no kitchen display". */
  readonly status: number | null;
  constructor(message: string, options?: { cause?: unknown; status?: number }) {
    super(message, options);
    this.name = "BoardFetchError";
    this.status = options?.status ?? null;
  }
}

export interface FetchBoardArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly stationId: string;
  readonly fetch?: typeof fetch;
}

export async function fetchBoard(args: FetchBoardArgs): Promise<readonly BoardTicket[]> {
  const get = args.fetch ?? fetch;
  const url =
    `${args.baseUrl}/v1/kitchen/board` +
    `?venueId=${encodeURIComponent(args.venueId)}` +
    `&stationId=${encodeURIComponent(args.stationId)}`;

  let response: Response;
  try {
    response = await get(url, { headers: { authorization: `Bearer ${args.apiKey}` } });
  } catch (error) {
    throw new BoardFetchError("the kitchen service is unreachable", { cause: error });
  }

  if (!response.ok) {
    throw new BoardFetchError(`the kitchen service answered ${response.status}`, { status: response.status });
  }

  let body: { tickets?: unknown };
  try {
    body = (await response.json()) as { tickets?: unknown };
  } catch (error) {
    throw new BoardFetchError("the kitchen service returned something that is not JSON", {
      cause: error,
    });
  }

  if (!Array.isArray(body.tickets)) {
    throw new BoardFetchError("the board has no tickets array");
  }

  // REFUSED WHOLESALE rather than filtered. Half a board is worse than a stale
  // one: a cook would work what is on the screen and never learn that a ticket
  // was dropped for being malformed.
  return body.tickets.map((raw, index) => {
    const ticket = raw as Partial<BoardTicket>;
    if (!ticket.ticketId || !ticket.orderId || !Array.isArray(ticket.items)) {
      throw new BoardFetchError(`tickets[${index}] is missing ticketId, orderId or items`);
    }
    return ticket as BoardTicket;
  });
}
